import { useState } from 'react';
import { useContacts } from '../hooks/useContacts';

// ============================================================
// Contacts Page - Address book for industry contacts
// Can add, edit, and delete contacts.
// The same form is used for both adding AND editing.
//
// ── FIELDS ──────────────────────────────────────────────────
//   phone     - tel: link  → opens phone dialer on mobile
//   email     - mailto:    → opens email app
//   instagram - opens instagram.com/<handle> in new tab
//   facebook  - opens facebook.com/<name> or a full URL in new tab
// ============================================================

function Contacts() {

  // ---- UI STATE ----
  const [showForm, setShowForm] = useState(false)
  // false = form hidden (default)
  // true  = form visible
  // toggled by the "+ Add Contact" button

  const [editingContact, setEditingContact] = useState(null)
  // null           = not editing (adding a new contact)
  // contact object = currently editing this contact


  // ---- HOOK - ALL CONTACT DATA AND DATABASE FUNCTIONS ----
  const { contacts, addContact, deleteContact, updateContact, loading, error } = useContacts()
  // contacts                   = array of all contact objects from Supabase
  // addContact / updateContact = write new or update existing row
  // deleteContact              = remove a row
  // loading                    = true while the first fetch is in-flight
  // error                      = any error string from the hook


  // ---- FORM FIELD STATE ----
  const [formData, setFormData] = useState({
    name:      '',
    pronouns:  '',
    phone:     '',
    email:     '',
    instagram: '',
    facebook:  '',
    location:  '',
  })
  // all fields start empty
  // handleEdit() fills these in when editing an existing contact


  // ---- SUBMIT / ERROR STATE ----
  const [submitError, setSubmitError]   = useState('')
  // '' = no error, any string = show that error below the form
  const [isSubmitting, setIsSubmitting] = useState(false)
  // true while waiting for a Supabase response (disables inputs + button)


  // -------------------------------------------------------
  // handleChange
  // Updates a single form field when the user types.
  // One function handles every input by reading e.target.name.
  // -------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target
    // e.target.name  = the input's name attribute (matches the formData key)
    // e.target.value = what the user just typed

    setFormData(prev => ({ ...prev, [name]: value }))
    // spread existing formData, then override just the changed field
    // [name] = computed property key -- uses the variable as the key name
  };


  // -------------------------------------------------------
  // handleEdit
  // Pre-fills the form with an existing contact's data and opens it.
  // -------------------------------------------------------
  const handleEdit = (contact) => {
    setEditingContact(contact)
    // remember which contact we're updating

    setFormData({
      name:      contact.name      || '',
      pronouns:  contact.pronouns  || '',
      phone:     contact.phone     || '',
      email:     contact.email     || '',
      instagram: contact.instagram || '',
      facebook:  contact.facebook  || '',
      location:  contact.location  || '',
      // || '' = use empty string if the DB value is null
      //         prevents "null" from appearing in the input
    })

    setShowForm(true)
    // open the form panel with the pre-filled data
  };


  // -------------------------------------------------------
  // handleSubmit
  // Saves the form -- adds a new contact or updates the existing one.
  // -------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault()
    // prevent browser page reload on form submit

    setSubmitError('')
    setIsSubmitting(true)

    try {
      if (editingContact) {
        await updateContact(editingContact.id, formData)
        alert('Contact updated successfully!')
      } else {
        await addContact(formData)
        alert('Contact added successfully!')
      }

      // ── RESET AFTER SUCCESSFUL SAVE ──────────────────────
      setFormData({ name: '', pronouns: '', phone: '', email: '', instagram: '', facebook: '', location: '' })
      setShowForm(false)
      setEditingContact(null)

    } catch (err) {
      setSubmitError(err.message || 'Error saving contact')
      console.error('Submit error:', err)

    } finally {
      setIsSubmitting(false)
      // always re-enable the form whether save succeeded or failed
    }
  };


  // -------------------------------------------------------
  // handleCancel
  // Closes and fully resets the form without saving.
  // -------------------------------------------------------
  const handleCancel = () => {
    setShowForm(false)
    setEditingContact(null)
    setFormData({ name: '', pronouns: '', phone: '', email: '', instagram: '', facebook: '', location: '' })
    setSubmitError('')
  };


  // -------------------------------------------------------
  // handleDelete
  // Deletes a contact after a browser confirmation dialog.
  // -------------------------------------------------------
  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return
    // user must click OK to proceed, Cancel stops the function

    try {
      await deleteContact(contactId)
      alert('Contact deleted successfully!')

    } catch (err) {
      alert('Error deleting contact: ' + (err.message || 'Unknown error'))
      console.error('Delete error:', err)
    }
  };


  // -------------------------------------------------------
  // instagramHandle
  // Strips a leading @ from a saved handle so the URL stays clean.
  // Works whether the user saved "kingperk" or "@kingperk".
  // -------------------------------------------------------
  const instagramHandle = (raw) => raw.replace(/^@/, '')
  // /^@/ = regex that matches @ only at the start of the string


  // -------------------------------------------------------
  // facebookHref
  // Builds a Facebook URL from whatever the user saved.
  //   Full URL already  → use as-is (they pasted https://facebook.com/...)
  //   Just a name       → prepend https://facebook.com/
  // -------------------------------------------------------
  const facebookHref = (raw) =>
    raw.startsWith('http') ? raw : `https://facebook.com/${raw}`


  // -------------------------------------------------------
  
  // -------------------------------------------------------


  // ---- LOADING STATE ----
  if (loading) {
    return (
      <div className="contacts-page">
        <h1 className="page-title">Contacts</h1>
        <p>Loading contacts...</p>
      </div>
    );
  }

  return (
    <div className="contacts-page">

      {/* ── PAGE HEADER ──────────────────────────────────────── */}
      <div className="page-header-row">
        <h1 className="page-title">Contacts</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          + Add Contact
        </button>
      </div>

      {/* ── ERROR DISPLAYS ───────────────────────────────────── */}
      {error       && <div className="text-danger">Error: {error}</div>}
      {submitError && <div className="text-danger">Error: {submitError}</div>}


      {/* ── ADD / EDIT FORM ──────────────────────────────────── */}
      {showForm && (
        <form onSubmit={handleSubmit} className="contact-form">

          <h2>{editingContact ? 'Edit Contact' : 'Add New Contact'}</h2>

          {/* ── NAME (required) ────────────────────────────── */}
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </div>

          {/* ── PRONOUNS ────────────────────────────────────── */}
          <div className="form-group">
            <label>Pronouns</label>
            <input
              type="text"
              name="pronouns"
              placeholder="they/them, she/her, he/him, ze/zir"
              value={formData.pronouns}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          {/* ── PHONE ───────────────────────────────────────── */}
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              // type="tel" = numeric keyboard on mobile
              name="phone"
              placeholder="(555) 867-5309"
              value={formData.phone}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          {/* ── EMAIL ───────────────────────────────────────── */}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              // type="email" = browser validates format on submit
              name="email"
              placeholder="name@example.com"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          {/* ── INSTAGRAM ───────────────────────────────────── */}
          <div className="form-group">
            <label>Instagram</label>
            <input
              type="text"
              name="instagram"
              placeholder="@username"
              value={formData.instagram}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          {/* ── FACEBOOK ────────────────────────────────────── */}
          <div className="form-group">
            <label>Facebook</label>
            <input
              type="text"
              name="facebook"
              placeholder="Name or full profile URL"
              value={formData.facebook}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          {/* ── LOCATION ────────────────────────────────────── */}
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              name="location"
              placeholder="City, State"
              value={formData.location}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          {/* ── FORM BUTTONS ────────────────────────────────── */}
          <div className="contact-form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingContact ? 'Update Contact' : 'Save Contact')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      )}


      {/* ── CONTACTS GRID ────────────────────────────────────── */}
      <div className="contacts-grid">
        {contacts.length === 0 ? (
          <p>No contacts yet. Add your first one!</p>
        ) : (
          contacts.map(contact => (
            <div key={contact.id} className="contact-card">

              {/* ── CARD HEADER: name + edit/delete buttons ──── */}
              <div className="contact-card-header">
                <div>
                  <h3>{contact.name}</h3>
                  {contact.pronouns && (
                    <p className="pronouns">{contact.pronouns}</p>
                  )}
                </div>

                <div className="contact-btn-group">
                  <button
                    onClick={() => handleEdit(contact)}
                    title="Edit contact"
                    className="btn-icon btn-icon--primary"
                  >
                    <i className="bi bi-pencil-fill"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(contact.id)}
                    title="Delete contact"
                    className="btn-icon btn-icon--danger"
                  >
                    <i className="bi bi-trash-fill"></i>
                  </button>
                </div>
              </div>


              {/* ── CONTACT DETAILS ──────────────────────────── */}
              {/* Each field only renders if it was saved (not empty/null) */}

              {/* ── PHONE -- opens phone dialer on mobile ─────── */}
              {contact.phone && (
                <p>
                  <strong><i className="bi bi-telephone-fill me-1"></i></strong>{' '}
                  <a href={`tel:${contact.phone}`} className="contact-link">
                    {/* tel: prompts the native phone dialer */}
                    {contact.phone}
                  </a>
                </p>
              )}

              {/* ── EMAIL -- opens default email app ─────────── */}
              {contact.email && (
                <p>
                  <strong><i className="bi bi-envelope-fill me-1"></i></strong>{' '}
                  <a href={`mailto:${contact.email}`} className="contact-link">
                    {/* mailto: prompts the native email app */}
                    {contact.email}
                  </a>
                </p>
              )}

              {/* ── INSTAGRAM -- opens profile in new tab ─────── */}
              {contact.instagram && (
                <p>
                  <strong><i className="bi bi-instagram me-1"></i></strong>{' '}
                  <a
                    href={`https://instagram.com/${instagramHandle(contact.instagram)}`}
                    // instagramHandle() strips any leading @ before building the URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    @{instagramHandle(contact.instagram)}
                    {/* always display with @ prefix regardless of how it was saved */}
                  </a>
                </p>
              )}

              {/* ── FACEBOOK -- opens profile in new tab ──────── */}
              {contact.facebook && (
                <p>
                  <strong><i className="bi bi-facebook me-1"></i></strong>{' '}
                  <a
                    href={facebookHref(contact.facebook)}
                    // facebookHref() handles both full URLs and plain names
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    {contact.facebook}
                  </a>
                </p>
              )}

              {/* ── LOCATION -- ──────── */}
              {contact.location && (
                <p>
                  <strong><i className="bi bi-geo-alt-fill me-1"></i></strong>{' '}
                    {contact.location}
                </p>
              )}

            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Contacts;